type ToastType = "success" | "error" | "warning" | "info";

interface ToastItem {
  id: number;
  msg: string;
  type: ToastType;
}

const VALID_TYPES: ToastType[] = ["success", "error", "warning", "info"];

// Severity drives dwell time. Floor at 5s so a glance away doesn't lose
// the message. Errors stick until dismissed.
const DURATIONS: Record<ToastType, number> = {
  success: 5000,
  info: 6000,
  warning: 8000,
  error: 0,
};

const ICONS: Record<ToastType, string> = {
  success: "check-circle-2",
  info: "info",
  warning: "triangle-alert",
  error: "octagon-x",
};

const MAX_VISIBLE = 5;
const DEDUPE_WINDOW_MS = 800;

// readAndClearFlash reads the one-shot `studio_flash` cookie (set server-side by
// SetFlashToast), clears it, and returns the payload — or null when absent.
function readAndClearFlash(): { msg: string; type: string } | null {
  const flash = document.cookie.split("; ").find((row) => row.startsWith("studio_flash="));
  if (!flash) return null;
  let payload: { msg: string; type: string } | null = null;
  try {
    const data = JSON.parse(decodeURIComponent(flash.substring("studio_flash=".length)));
    if (data && data.msg) {
      payload = { msg: data.msg, type: data.type || "success" };
    }
  } catch (e) {
    console.error("Failed to parse flash cookie", e);
  }
  document.cookie = "studio_flash=; path=/; Max-Age=0";
  return payload;
}

// registerFlashCookieBridge makes SetFlashToast work across HTMX boost navigations
// (HX-Location). The toast wrapper is hx-preserve'd, so the store's Alpine init()
// — which reads the flash cookie — only runs on a full page load, never on a boost.
// This re-reads the cookie after every htmx settle and re-dispatches it as the
// `flash-message` event the (preserved) store already listens for. The store's
// dedupe window absorbs any overlap with init() on the rare full-load + settle case.
export function registerFlashCookieBridge(): void {
  // Listen on `document` (always present, even pre-body) — htmx events bubble to it.
  document.addEventListener("htmx:afterSettle", () => {
    const flash = readAndClearFlash();
    if (flash) {
      window.dispatchEvent(new CustomEvent("flash-message", { detail: flash }));
    }
  });
}

export function toastStore() {
  return {
    toasts: [] as ToastItem[],
    _seq: 0,
    _timers: {} as Record<number, number>,
    _elapsed: {} as Record<number, number>,
    _resumedAt: {} as Record<number, number>,
    _lastSig: "" as string,
    _lastAt: 0,
    // True when the browser supports the Popover API and we've promoted the
    // toast stack to a `popover=manual`. A native modal `<dialog>` (showModal)
    // renders in the browser TOP LAYER, which sits above ANY z-index — so a
    // plain `fixed; z-popover` stack is always painted behind an open modal
    // (e.g. the CMS image picker). Promoting the stack to the top layer too is
    // the only way to make toasts visible over a modal. Progressive
    // enhancement: when unsupported, the stack stays a normal fixed element
    // (its prior behaviour — fine everywhere except over a modal).
    _popover: false,

    add(msg: string, type: string = "info") {
      if (!msg) return;
      const t: ToastType = (VALID_TYPES as string[]).includes(type) ? (type as ToastType) : "info";

      const sig = `${t}::${msg}`;
      const now = Date.now();
      if (sig === this._lastSig && now - this._lastAt < DEDUPE_WINDOW_MS) return;
      this._lastSig = sig;
      this._lastAt = now;

      const id = ++this._seq;
      this.toasts.push({ id, msg, type: t });
      // Promote (or re-promote) the stack into the top layer so this toast
      // paints above any open modal <dialog>. Re-raising on every add keeps
      // the stack above a modal that opened AFTER an earlier toast.
      this._raise();

      while (this.toasts.length > MAX_VISIBLE) {
        const dropped = this.toasts.shift();
        if (dropped) this._clear(dropped.id);
      }

      const dur = DURATIONS[t];
      if (dur > 0) {
        this._elapsed[id] = 0;
        this._resumedAt[id] = now;
        this._timers[id] = window.setTimeout(() => this.remove(id), dur);
      }

      const self = this as any;
      if (self.$nextTick) {
        self.$nextTick(() => {
          const refresh = (window as any).refreshLucideIcons;
          if (refresh) refresh(self.$el);
        });
      }
    },

    remove(id: number) {
      this.toasts = this.toasts.filter((x) => x.id !== id);
      this._clear(id);
      // Empty stack → leave the top layer so it can't block anything below.
      if (this.toasts.length === 0) this._lower();
    },

    // _raise promotes the stack element to the top layer (above modal dialogs)
    // via the Popover API. hide-then-show re-promotes an already-open popover to
    // the TOP of the top-layer stack (showPopover throws on an open popover, so
    // we close first). No-op when popover is unsupported. Wrapped in try/catch:
    // a popover call must never break toast delivery.
    _raise() {
      if (!this._popover) return;
      const el = (this as any).$el as HTMLElement & {
        showPopover?: () => void;
        hidePopover?: () => void;
      };
      if (!el || typeof el.showPopover !== "function") return;
      try {
        if (el.matches(":popover-open")) el.hidePopover!();
        el.showPopover();
      } catch {
        /* popover already showing / detached — ignore */
      }
    },

    _lower() {
      if (!this._popover) return;
      const el = (this as any).$el as HTMLElement & { hidePopover?: () => void };
      if (!el || typeof el.hidePopover !== "function") return;
      try {
        if (el.matches(":popover-open")) el.hidePopover();
      } catch {
        /* not open — ignore */
      }
    },

    pause(id: number) {
      if (!this._timers[id]) return;
      clearTimeout(this._timers[id]);
      delete this._timers[id];
      const startedAt = this._resumedAt[id] ?? Date.now();
      this._elapsed[id] = (this._elapsed[id] ?? 0) + (Date.now() - startedAt);
    },

    resume(id: number) {
      const toast = this.toasts.find((x) => x.id === id);
      if (!toast) return;
      const dur = DURATIONS[toast.type];
      if (dur <= 0 || this._timers[id]) return;
      const remaining = Math.max(0, dur - (this._elapsed[id] ?? 0));
      this._resumedAt[id] = Date.now();
      this._timers[id] = window.setTimeout(() => this.remove(id), remaining);
    },

    durationFor(type: ToastType): number {
      return DURATIONS[type] ?? 0;
    },

    _clear(id: number) {
      if (this._timers[id]) {
        clearTimeout(this._timers[id]);
        delete this._timers[id];
      }
      delete this._elapsed[id];
      delete this._resumedAt[id];
    },

    iconFor(type: ToastType): string {
      return ICONS[type] || "info";
    },

    ariaRole(type: ToastType): string {
      return type === "error" ? "alert" : "status";
    },

    ariaLive(type: ToastType): string {
      return type === "error" ? "assertive" : "polite";
    },

    init() {
      // Feature-detect the Popover API and, if present, mark the stack element
      // as a manual popover. We do this in JS (not in the template) so an
      // unsupported browser is never left with a `display:none` popover it can't
      // show — it simply keeps the normal fixed-position stack. The element keeps
      // its own bottom-right positioning via .studio-toast-stack (which also
      // neutralises the [popover] UA box styles).
      this._popover = typeof (HTMLElement.prototype as any).showPopover === "function";
      if (this._popover) {
        ((this as any).$el as HTMLElement).setAttribute("popover", "manual");
      }
      // Full-page-load path. Boosted (HX-Location) navigations are handled by
      // registerFlashCookieBridge, since this preserved store does not re-init.
      const flash = readAndClearFlash();
      if (flash) {
        this.add(flash.msg, flash.type);
      }
    },
  };
}
